import { Badge, Box, Flex, Text } from '@chakra-ui/react';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { colors } from 'packages/common-frontend/colors';
import { DEAL_STATUS_DISPLAY, periodStatus } from '~/backup/shared/dealStatus';
import { formatPeriodShort } from '~/backup/shared/format';
import {
  DealPeriod,
  periodPath,
} from '~/backup/shared/views/deal-overview/types';
import { Icon } from '~/packages/common-frontend/components/icons';

/**
 * The deal's periods as a row of tabs, newest first like the table below: each
 * names its month and status, and selects that period on this page. Scrolls
 * sideways once a deal has more periods than fit.
 */
export function PeriodStrip({
  implementationId,
  periods,
  current,
  selected,
  today,
}: {
  implementationId: string;
  periods: DealPeriod[];
  current: DealPeriod | null;
  selected: DealPeriod;
  today: string;
}): React.ReactElement {
  return (
    <Flex
      as="nav"
      aria-label="Servicing periods"
      gap="0.5rem"
      overflowX="auto"
      pb="0.25rem"
    >
      {periods.map((period) => {
        const status = DEAL_STATUS_DISPLAY[periodStatus(period, today)];
        const isSelected = period.reportingPeriod === selected.reportingPeriod;
        return (
          <Box
            key={period.reportingPeriod}
            as={RouterLink}
            to={periodPath({ implementationId, period, current })}
            aria-current={isSelected ? 'page' : undefined}
            title={status.label}
            flex="0 0 auto"
            borderWidth="1px"
            borderRadius="md"
            borderColor={isSelected ? colors.blue[400] : colors.gray[100]}
            bg={isSelected ? colors.blue[50] : undefined}
            px="0.75rem"
            py="0.5rem"
            _hover={{ borderColor: colors.blue[300] }}
          >
            <Flex align="center" gap="0.4rem">
              <Box fontSize="0.8rem">
                <Icon icon={status.icon} type="solid" color={status.color} />
              </Box>
              <Text fontWeight={600} fontSize="0.85rem">
                {formatPeriodShort(period.reportingPeriod)}
              </Text>
              {period.reportingPeriod === current?.reportingPeriod && (
                <Badge colorScheme="blue" fontSize="0.55rem">
                  Current
                </Badge>
              )}
            </Flex>
            <Text color={colors.gray[500]} fontSize="0.7rem" mt="0.1rem">
              {status.label}
            </Text>
          </Box>
        );
      })}
    </Flex>
  );
}
